<script setup lang="ts">
import type { ResolvedRange } from '@/lib/charts/timeRange'
import type { Customer, FleetTimelineRow, FleetTimelineSegment, HealthState } from '@/types'
import { Link } from '@inertiajs/vue3'
import { format } from 'date-fns'
import { computed } from 'vue'
import { healthPresentation } from '@/lib/health'
import { adminInstancePath } from '@/routes'

interface TimelineInstance {
  id: number
  identifier: string | null
  customer: Pick<Customer, 'name'>
}

const props = defineProps<{
  timeline: FleetTimelineRow[]
  instances: TimelineInstance[]
  resolved: ResolvedRange
}>()

const instanceLookup = computed(() => new Map(props.instances.map(instance => [instance.id, instance])))

interface Row {
  timeline: FleetTimelineRow
  instance: TimelineInstance
}

const rows = computed<Row[]>(() =>
  props.timeline
    .flatMap((timeline) => {
      const instance = instanceLookup.value.get(timeline.instance_id)
      return instance ? [{ timeline, instance }] : []
    })
    .sort((a, b) => a.instance.customer.name.localeCompare(b.instance.customer.name)),
)

const span = computed(() => Math.max(props.resolved.to - props.resolved.from, 1))

const LEGEND_STATES: HealthState[] = ['ok', 'degraded', 'down', 'never_reported']

function widthPercent(segment: FleetTimelineSegment): number {
  return ((segment.to - segment.from) / span.value) * 100
}

function tooltip(segment: FleetTimelineSegment): string {
  const label = healthPresentation(segment.status).label
  const from = format(new Date(segment.from), 'd MMM HH:mm')
  const to = format(new Date(segment.to), 'd MMM HH:mm')
  return `${label} · ${from} – ${to}`
}
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex max-h-80 flex-col gap-2 overflow-y-auto pr-1">
      <Link
        v-for="{ timeline: row, instance } in rows"
        :key="row.instance_id"
        :href="adminInstancePath(instance.id)"
        class="hover:bg-elevated/50 -mx-2 flex items-center gap-3 rounded-md px-2 py-1 transition"
      >
        <div
          class="text-toned w-40 shrink-0 truncate text-sm"
          :title="`${instance.customer.name} · ${instance.identifier ?? '—'}`"
        >
          {{ instance.customer.name }}
        </div>
        <div class="bg-elevated flex h-3 flex-1 overflow-hidden rounded-full">
          <div
            v-for="(segment, index) in row.segments"
            :key="index"
            class="h-full first:rounded-l-full last:rounded-r-full"
            :style="{ width: `${widthPercent(segment)}%`, backgroundColor: healthPresentation(segment.status).chartColor }"
            :title="tooltip(segment)"
          />
        </div>
      </Link>
    </div>
    <ul class="mt-1 flex flex-wrap gap-x-4 gap-y-2">
      <li v-for="state in LEGEND_STATES" :key="state" class="flex items-center gap-2 text-sm">
        <span class="size-2.5 rounded-full" :style="{ backgroundColor: healthPresentation(state).chartColor }" />
        <span class="text-toned">{{ healthPresentation(state).label }}</span>
      </li>
    </ul>
  </div>
</template>
